/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { makeStyles } from '@mui/styles';
import CircularProgress from 'apollo-react/components/CircularProgress';
import Search from 'apollo-react-icons/Search';
import classNames from 'classnames';
import _ from 'lodash';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';

import ZoomDialog from './ZoomDialog';
import { ZOOM_STYLES } from '../../../constants/CommonStylesConstants';
import { useFormSelector } from '../../../custom-hooks';
import { useWindowDimensions } from '../../../custom-hooks/useWindowDimensions';
import { getIsRtlLanguage } from '../../../selectors/AuthSelectors';
import { getUITA, isTrue } from '../../../utils/commonUtils';
import { bulkAddingNewRows, setUserMadeInteraction } from '../../actions/runtime.actions';
import { DATALOOKUP_TYPE_EXTERNAL_API } from '../../constants/form-manager.constants';
import { SubGridContext } from '../../context';
import { getIsFormChangesMode } from '../../selectors/common.selectors';
import { getSsContext } from '../../selectors/form-manager.selectors';
import {
  getDynamicParamCollectionSelector,
  getSavedSearchTitle,
} from '../../selectors/runtime.selectors';
import { createChangesObj } from '../../utils/controls/ssZoom.utils';
import { transformFiltersForAPI } from '../../utils/runtime.utils';

const useStyles = makeStyles(ZOOM_STYLES);

function SSRuntimeZoom({
  className,
  multiSelect,
  dataObject,
  dataLookUpType,
  dataMappingList,
  filter,
  actions,
  id,
  index,
  inTable,
  inFieldGroup,
  isHiddenLabel,
  formKey,
  searchName = '',
  uniqueIdentifier,
  isAutoCompleteLoading,
  textBoxPropsJson = '{}',
  isStandalone,
  isOnline,
}) {
  const { isMobileDevice } = useWindowDimensions();
  const dispatch = useDispatch();
  const [dialogOpen, setDialogOpen] = useState(false);
  const title = useFormSelector(getSavedSearchTitle, { id });
  const { entityName, isTextBoxViewOnly } = JSON.parse(textBoxPropsJson);
  const ssContext = useFormSelector(getSsContext, { index, entityName });
  const dynamicParamCollection = useFormSelector(getDynamicParamCollectionSelector, {
    index,
    entityName,
    id,
  });
  const inSubGrid = useContext(SubGridContext);
  const isFormChangesMode = useFormSelector(getIsFormChangesMode);
  const isRtlLanguage = useSelector(getIsRtlLanguage);
  const classes = useStyles();
  const inSubGridOrMobile = inSubGrid || isMobileDevice;
  const isMultiSelect = isTrue(multiSelect);
  const isInTable = inTable || (inFieldGroup && isHiddenLabel);
  const searchTableTitle = title || searchName || dataObject;
  const renderTitle =
    dataLookUpType === DATALOOKUP_TYPE_EXTERNAL_API
      ? `External API - ${dataObject ?? ''}`
      : searchTableTitle;

  const handleOnSelect = useCallback(
    (selection, columns) => {
      const changesToApply = [];

      if (isMultiSelect) {
        if (!selection.length) {
          setDialogOpen(false);
          return;
        }

        selection.forEach((item, i) => {
          const rowIndex = [...index.slice(0, -1), _.last(index) + i];
          changesToApply.push(
            ...dataMappingList.reduce(
              createChangesObj(rowIndex, item, ssContext.entityDefinition, columns),
              []
            )
          );
        });
      } else {
        changesToApply.push(
          ...dataMappingList.reduce(
            createChangesObj(index, selection, ssContext.entityDefinition, columns),
            []
          )
        );
      }

      batch(() => {
        dispatch(setUserMadeInteraction(formKey));

        if (isMultiSelect && selection.length > 1) {
          const newRows = Array(selection.length - 1).fill({});
          dispatch(
            bulkAddingNewRows({
              entityName,
              index,
              newRows,
              insertMode: true,
              formKey,
            })
          );
        }

        actions.bulkUpdateControlValues(changesToApply);
      });

      setDialogOpen(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [`${index}`]
  );

  const referencedColumns = useMemo(
    () =>
      Array.isArray(dataMappingList) &&
      dataMappingList.reduce(
        (refColumns, mapping) => ({ ...refColumns, [mapping.updateTo]: mapping.updateFrom }),
        {}
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const filterConditions =
    filter?.Conditions &&
    filter.Conditions.map((condition) => {
      return { ...condition, Operator: condition.SqlOperator };
    });

  const customFilter = filterConditions && transformFiltersForAPI(filterConditions, ssContext);

  return isTextBoxViewOnly && !isStandalone && !isFormChangesMode ? null : (
    <>
      <span
        className={classNames(
          classes.root,
          !isStandalone && className,
          isInTable && !isStandalone ? classes.inTable : '',
          inSubGridOrMobile && !isInTable ? classes.inSubGridOrMobile : '',
          isRtlLanguage ? classes.rightAlignIcons : ''
        )}
        onClick={() => !isAutoCompleteLoading && isOnline && setDialogOpen(true)}
        ui-ta={getUITA('FormCtrl', 'SSZoom', dataObject, uniqueIdentifier)}
        data-ss-id={id}
      >
        <div>
          {isAutoCompleteLoading ? (
            <CircularProgress color="inherit" variant="indeterminate" size={24} />
          ) : (
            <Search className={classes.searchIcon} color={isOnline ? 'primary' : 'disabled'} />
          )}
        </div>
      </span>
      <ZoomDialog
        open={dialogOpen}
        onSelect={handleOnSelect}
        onClose={() => setDialogOpen(false)}
        multiSelect={isMultiSelect}
        entityName={dataObject}
        initialSearchName={searchName}
        dataKey={id}
        customFilter={customFilter}
        title={renderTitle}
        referencedColumns={referencedColumns}
        translatedSearchName={searchTableTitle}
        dynamicParamCollection={dynamicParamCollection}
        dataLookUpType={dataLookUpType}
      />
    </>
  );
}

export default SSRuntimeZoom;
